// The town square: backdrop, circle, pop-out panels and one control bar (the Storyteller's or the
// player's). Holds no game state beyond which panels and dialog are open; everything else is
// `view`, the server's last projection for this viewer.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Copy, X } from "lucide-react";
import { useGame } from "../state/game.js";
import { Backdrop } from "../components/townsquare/Backdrop.js";
import { Circle } from "../components/townsquare/Circle.js";
import { RolesInPlay } from "../components/panels/RolesInPlay.js";
import { NightOrder } from "../components/panels/NightOrder.js";
import { Nominations } from "../components/panels/Nominations.js";
import { NotepadToggle, NotepadWindow } from "../components/panels/Notepad.js";
import { NeighborChat } from "../components/panels/NeighborChat.js";
import { ControlPanel } from "../components/storyteller/ControlPanel.js";
import { SeatDialog } from "../components/storyteller/SeatDialog.js";
import { GuessDialog } from "../components/player/GuessDialog.js";
import { PlayerBar } from "../components/player/PlayerBar.js";
import { Button } from "../components/ui/primitives.js";
import { cn } from "../lib/ui.js";

export function GamePage() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const { view, connected, joinGame, leaveGame, notices, dismissNotice, lastError, clearError, send } =
    useGame();

  const [openPanels, setOpenPanels] = useState({
    roles: false,
    night: true,
    nominations: true,
    notepad: false,
    neighbours: false,
  });
  const [seatDialogId, setSeatDialogId] = useState<string | null>(null);
  const [guessSeatId, setGuessSeatId] = useState<string | null>(null);
  const [nominating, setNominating] = useState<string | null>(null);

  // Re-join on refresh or reconnect without stating a side, so the server puts us back where we
  // were; a preference here would turn an eligible player into a Storyteller.
  useEffect(() => {
    if (!connected || !code) return;
    if (view?.code === code) return;
    void joinGame(code).then((ack) => {
      if (!ack.ok) navigate("/");
    });
  }, [connected, code, view?.code, joinGame, navigate]);

  if (!view || view.code !== code) {
    return (
      <div className="grid min-h-full place-items-center text-sm text-parchment-dim">
        {connected ? "Joining…" : "Connecting…"}
      </div>
    );
  }

  const isStoryteller = view.you.isStoryteller;
  const seats = view.seats;
  const mySeat = seats.find((s) => s.id === view.you.seatId) ?? null;
  const nominationsOpen =
    !isStoryteller &&
    view.phase.kind === "day" &&
    view.daySubPhase === "nominations" &&
    view.openNominationId === null;

  function onSeatClick(seatId: string): void {
    if (isStoryteller) {
      setSeatDialogId(seatId);
      return;
    }
    // Dead players can still call for a Traveler's exile.
    const target = seats.find((s) => s.id === seatId);
    if (nominationsOpen && mySeat && (mySeat.alive || target?.isTraveler)) setNominating(seatId);
  }

  const nominee = view.seats.find((s) => s.id === nominating) ?? null;

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <Backdrop phase={view.phase} />

      {/* --- top bar --- */}
      <header className="relative z-10 flex items-center gap-3 px-4 py-2 text-sm">
        <Link to="/" className="text-parchment-dim hover:text-parchment">
          ← Home
        </Link>
        <span className="font-mono tracking-widest text-brass">{view.code}</span>
        <button
          type="button"
          title="Copy the code"
          onClick={() => void navigator.clipboard?.writeText(view.code)}
          className="text-parchment-dim hover:text-parchment"
        >
          <Copy className="size-3.5" />
        </button>
        <span className="text-parchment-dim">
          {view.phase.kind === "setup" ? "Setup" : `${view.phase.kind === "night" ? "Night" : "Day"} ${view.phase.number}`}
          {view.phase.kind === "day" && view.daySubPhase === "nominations" && " · nominations open"}
        </span>
        <span className="text-parchment-dim">
          {view.livingCount} alive · {view.voteThreshold} to execute
        </span>
        {view.storytellerNames.length > 0 && (
          <span className="text-parchment-dim">ST: {view.storytellerNames.join(", ")}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {!connected && <span className="text-evil">reconnecting…</span>}
          {view.status === "finished" && (
            <span className="rounded bg-brass/20 px-2 py-0.5 text-brass">
              {view.winningTeam === "good" ? "Good wins" : "Evil wins"}
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              leaveGame();
              navigate("/");
            }}
          >
            Leave
          </Button>
        </div>
      </header>

      {/* --- Storyteller help pings --- */}
      {isStoryteller && (view.storyteller?.helpRequests.length ?? 0) > 0 && (
        <div className="relative z-10 mx-4 mb-1 space-y-1">
          {view.storyteller!.helpRequests.map((request) => (
            <div
              key={request.seatId}
              className="flex items-center gap-2 rounded border border-brass/60 bg-brass/15 px-3 py-1.5 text-sm"
            >
              <span className="font-bold text-brass">!</span>
              <span>{request.displayName} is asking for the Storyteller</span>
              <Button
                size="sm"
                className="ml-auto"
                onClick={() => void send("st:acceptHelp", { seatId: request.seatId })}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void send("st:dismissHelp", { seatId: request.seatId })}
              >
                Reject
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* --- the square --- */}
      {/* The panel columns sit in the flow either side of the circle rather than floating over it:
          at this scale an overlay hid the seats behind them, and a seat you cannot click is worse
          than a circle that is a little smaller. */}
      <main className="relative z-10 flex min-h-0 flex-1 items-stretch justify-center gap-4 px-4 pb-2">
        <div className="flex w-88 shrink-0 flex-col gap-2 overflow-y-auto py-2 cc-scroll">
          {isStoryteller && (
            <NightOrder
              view={view}
              open={openPanels.night}
              onOpenChange={(open) => setOpenPanels((p) => ({ ...p, night: open }))}
            />
          )}
          <Nominations
            view={view}
            open={openPanels.nominations}
            onOpenChange={(open) => setOpenPanels((p) => ({ ...p, nominations: open }))}
          />
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-center">
          <Circle
            view={view}
            onSeatClick={onSeatClick}
            onGuessClick={setGuessSeatId}
            {...(isStoryteller
              ? {
                  onMoveSeat: (seatId: string, direction: -1 | 1) =>
                    void send("st:moveSeat", { seatId, direction }),
                  onRemoveSeat: (seatId: string) => void send("st:removeSeat", { seatId }),
                }
              : {})}
          />
        </div>

        <div className="flex w-88 shrink-0 flex-col py-2">
          {/* min-h-0 + flex-1 is what lets this scroll inside the column; without it the stack just
              grew past the bottom of <main> and the last panel was clipped, not scrolled. */}
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto cc-scroll">
            <RolesInPlay
              view={view}
              open={openPanels.roles}
              onOpenChange={(open) => setOpenPanels((p) => ({ ...p, roles: open }))}
            />
            <NotepadToggle
              open={openPanels.notepad}
              onOpenChange={(open) => setOpenPanels((p) => ({ ...p, notepad: open }))}
            />
            <NeighborChat
              view={view}
              open={openPanels.neighbours}
              onOpenChange={(open) => setOpenPanels((p) => ({ ...p, neighbours: open }))}
            />
          </div>

          {(lastError || notices.length > 0) && (
            <div className="mt-auto flex flex-col gap-2 pt-2">
              {lastError && (
                <div className="flex items-start gap-2 rounded border border-evil/70 bg-ink-soft px-3 py-2 text-xs">
                  <span className="flex-1 text-parchment">{lastError}</span>
                  <button type="button" onClick={clearError} className="text-parchment-dim hover:text-parchment">
                    <X className="size-4" />
                  </button>
                </div>
              )}
              {notices.map((notice) => (
                <div
                  key={notice.id}
                  className={cn(
                    "flex items-start gap-2 rounded border bg-ink-soft px-3 py-2 text-xs",
                    notice.kind === "warn" ? "border-brass/60" : "border-ink-line",
                  )}
                >
                  <span className="flex-1 text-parchment">{notice.text}</span>
                  <button
                    type="button"
                    onClick={() => dismissNotice(notice.id)}
                    className="text-parchment-dim hover:text-parchment"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* --- controls --- */}
      <footer className="relative z-10 p-3">
        {isStoryteller ? <ControlPanel view={view} /> : <PlayerBar view={view} />}
      </footer>

      {/* --- nomination confirm --- */}
      {nominee && (
        <div className="fixed bottom-24 left-1/2 z-30 -translate-x-1/2 rounded-lg border border-brass/60 bg-ink-soft px-4 py-2 shadow-xl">
          <p className="mb-2 text-sm">
            {nominee.isTraveler ? `Call for ${nominee.displayName}'s exile?` : `Nominate ${nominee.displayName}?`}
          </p>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                await send("player:nominate", { nomineeSeatId: nominee.id });
                setNominating(null);
              }}
            >
              {nominee.isTraveler ? "Call for exile" : "Nominate"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNominating(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {openPanels.notepad && (
        <NotepadWindow view={view} onClose={() => setOpenPanels((p) => ({ ...p, notepad: false }))} />
      )}

      <SeatDialog view={view} seatId={seatDialogId} onClose={() => setSeatDialogId(null)} />
      <GuessDialog view={view} seatId={guessSeatId} onClose={() => setGuessSeatId(null)} />
    </div>
  );
}
